'use client';

import { c as _c } from "react/compiler-runtime";
import { startTransition, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import createTVSlidesAPI from "@/services/tv-slides";
import RenderSlide from "./render-slide";
const enterMap = {
  "slide-in-right": {
    opacity: 0,
    x: "50%"
  },
  "slide-in-left": {
    opacity: 0,
    x: "-50%"
  },
  "slide-in-up": {
    opacity: 0,
    y: "-50%"
  },
  "slide-in-down": {
    opacity: 0,
    y: "50%"
  },
  "fade-in": {
    opacity: 0,
    mode: "wait"
  }
};
const exitMap = {
  "slide-out-right": {
    opacity: 0,
    x: "50%"
  },
  "slide-out-left": {
    opacity: 0,
    x: "-50%"
  },
  "slide-out-up": {
    opacity: 0,
    y: "-50%"
  },
  "slide-out-down": {
    opacity: 0,
    y: "50%"
  },
  "fade-out": {
    opacity: 0,
    mode: "wait"
  }
};
const makeVariants = (enterName, exitName) => {
  const enter = enterName && enterMap[enterName];
  const exit = exitName && exitMap[exitName];
  return {
    enter,
    center: {
      opacity: 1,
      x: 0,
      y: 0
    },
    exit,
    mode: enter.mode ?? "popLayout"
  };
};
const SlideShow = () => {
  const $ = _c(31);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [slides, setSlides] = useState(t0);
  const [newSlides, setNewSides] = useState(undefined);
  const [currentSlide, setCurrentSlide] = useState(0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => startTransition(() => setCurrentSlide(_temp));
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const setNextSlide = t1;
  const fetchPending = useRef(false);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = createTVSlidesAPI();
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const api = t2;
  let t3;
  let t4;
  if ($[3] !== currentSlide || $[4] !== slides) {
    t3 = () => {
      if (currentSlide >= slides.length) {
        return;
      }
      const timeout = setTimeout(() => {
        setNextSlide();
      }, slides[currentSlide].display_duration * 1000);
      return () => {
        clearTimeout(timeout);
      };
    };
    t4 = [currentSlide, slides];
    $[3] = currentSlide;
    $[4] = slides;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== currentSlide || $[8] !== newSlides || $[9] !== slides.length) {
    t5 = () => {
      const fetchSlides = async () => {
        fetchPending.current = true;
        const newSlides_ = await api.get();
        setNewSides(newSlides_);
        try {
          await Promise.all(newSlides_.map(_temp2));
        } catch {}
        if (slides.length == 0) {
          setSlides(newSlides_);
        }
        fetchPending.current = false;
      };
      if (currentSlide == 0) {
        if (!fetchPending.current) {
          fetchSlides();
        }
      } else {
        if (currentSlide >= slides.length) {
          if (!fetchPending.current && newSlides) {
            setSlides(newSlides);
          }
          startTransition(() => setCurrentSlide(0));
        }
      }
    };
    $[7] = currentSlide;
    $[8] = newSlides;
    $[9] = slides.length;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== currentSlide) {
    t6 = [currentSlide];
    $[11] = currentSlide;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  useEffect(t5, t6);
  if (slides.length == 0) {
    return null;
  }
  const current_0 = currentSlide >= slides.length ? slides.length - 1 : currentSlide;
  const slide_0 = slides[current_0];
  let t7;
  if ($[13] !== slide_0.enter_animation || $[14] !== slide_0.exit_animation) {
    t7 = makeVariants(slide_0.enter_animation, slide_0.exit_animation);
    $[13] = slide_0.enter_animation;
    $[14] = slide_0.exit_animation;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let mode;
  let variants;
  if ($[16] !== t7) {
    ({
      mode,
      ...variants
    } = t7);
    $[16] = t7;
    $[17] = mode;
    $[18] = variants;
  } else {
    mode = $[17];
    variants = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      position: "absolute",
      inset: 0,
      willChange: "transform, opacity"
    };
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t10;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      duration: 0.6,
      ease: "easeOut"
    };
    t10 = {
      opacity: 1,
      x: 0,
      y: 0
    };
    $[20] = t10;
    $[21] = t9;
  } else {
    t10 = $[20];
    t9 = $[21];
  }
  let t11;
  if ($[22] !== slide_0) {
    t11 = <RenderSlide slide={slide_0} />;
    $[22] = slide_0;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== slide_0.id || $[25] !== t11 || $[26] !== variants) {
    t12 = <motion.div key={slide_0.id} style={t8} variants={variants} initial="enter" exit="exit" transition={t9} animate={t10}>{t11}</motion.div>;
    $[24] = slide_0.id;
    $[25] = t11;
    $[26] = variants;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== mode || $[29] !== t12) {
    t13 = <AnimatePresence mode={mode}>{t12}</AnimatePresence>;
    $[28] = mode;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
};
export default SlideShow;
function _temp(current) {
  return current + 1;
}
function _temp2(slide) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject();
    img.src = `https://www.alexandria.cz/${slide.attributes.filePath}`;
  });
}